import {
  AccountState,
  AccountType,
  CustomerAccountUser,
  Self,
} from "@generated/types";

export type ExtendedSelf = Self & {
  fullName: string;
  initials: string;
};

/**
 * Selects a default account ID: prefers an OPEN account first (so users
 * with a mix of OPEN and non-OPEN accounts land on the accessible one),
 * then prefers corporate, then falls back to the first account.
 */
export function selectDefaultAccount(
  accounts: CustomerAccountUser[]
): string | null {
  const openAccounts = accounts.filter(
    (account) =>
      (account.customerAccount?.accountState as string) === AccountState.Open
  );

  if (openAccounts.length > 0) {
    const openCorporate = openAccounts.find(
      (account) =>
        (account.customerAccount?.accountType as string) ===
        AccountType.Corporate
    );
    return openCorporate?.id || openAccounts[0]?.id || null;
  }

  const corporateAccount = accounts.find(
    (account) =>
      (account.customerAccount?.accountType as string) === AccountType.Corporate
  );
  return corporateAccount?.id || accounts[0]?.id || null;
}

/**
 * Extends a Self profile with computed fullName and initials.
 */
export function extendProfile(raw: Self): ExtendedSelf {
  const fullName = `${raw.firstName} ${raw.lastName}`.trim();
  const initials = fullName
    .split(" ")
    .map((name) => name[0])
    .join("");
  return {
    ...raw,
    fullName,
    initials,
  } as ExtendedSelf;
}

/**
 * Derives booking entity flags from a customer account's bookingEntityCodes.
 * Each capability is a GROUP of booking entity codes (e.g. digital assets =
 * UT or UM) — any one code of the group enables the capability.
 */
export function deriveBookingEntityFlags(
  customerAccount: CustomerAccountUser | null,
  bookingEntityConfig: {
    CURRENCIES: readonly string[];
    DIGITAL_ASSETS: readonly string[];
  }
): {
  hasCurrencies: boolean;
  hasDigitalAssets: boolean;
  hasBothEntities: boolean;
} {
  const bookingEntityCodes =
    (customerAccount?.customerAccount as { bookingEntityCodes?: string[] })
      ?.bookingEntityCodes || [];
  const hasCurrencies = bookingEntityConfig.CURRENCIES.some((code) =>
    bookingEntityCodes.includes(code)
  );
  const hasDigitalAssets = bookingEntityConfig.DIGITAL_ASSETS.some((code) =>
    bookingEntityCodes.includes(code)
  );
  return {
    hasCurrencies,
    hasDigitalAssets,
    hasBothEntities: hasCurrencies && hasDigitalAssets,
  };
}
